import React, { useEffect, useMemo, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Sparkles,
  Save,
  Inbox as InboxIcon,
  Settings,
} from 'lucide-react';
import {
  getMyWidget,
  upsertMyWidget,
  isSlugAvailable,
  slugify,
  publicUrlForSlug,
} from '../services/widget';
import { listMyLeads } from '../services/leads';
import type { AgentWidget } from '../types';
import Inbox from './Inbox';

interface DashboardProps {
  user: User;
}

interface FormState {
  brand_name: string;
  slug: string;
  primary_colour: string;
  contact_phone: string;
  contact_email: string;
  office_address: string;
  logo_url: string;
  disclaimer_text: string;
}

const EMPTY_FORM: FormState = {
  brand_name: '',
  slug: '',
  primary_colour: '#f47a6b',
  contact_phone: '',
  contact_email: '',
  office_address: '',
  logo_url: '',
  disclaimer_text: '',
};

type Tab = 'inbox' | 'widget';

export default function Dashboard({ user }: DashboardProps) {
  const [loading, setLoading] = useState(true);
  const [widget, setWidget] = useState<AgentWidget | null>(null);
  const [tab, setTab] = useState<Tab>('inbox');
  const [openLeadCount, setOpenLeadCount] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'ok' | 'taken' | 'invalid'>(
    'idle'
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // Load existing widget + lead count in parallel on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getMyWidget(user.id), listMyLeads()]).then(([w, leads]) => {
      if (cancelled) return;
      setLoading(false);

      const openCount = leads.filter(
        (l) => l.status === 'new' || l.status === 'contacted' || l.status === 'booked'
      ).length;
      setOpenLeadCount(openCount);

      if (w) {
        setWidget(w);
        setForm({
          brand_name: w.brand_name ?? '',
          slug: w.slug ?? '',
          primary_colour: w.primary_colour ?? '#f47a6b',
          contact_phone: w.contact_phone ?? '',
          contact_email: w.contact_email ?? user.email ?? '',
          office_address: w.office_address ?? '',
          logo_url: w.logo_url ?? '',
          disclaimer_text: w.disclaimer_text ?? '',
        });
        setSlugTouched(true);
        setSlugStatus('ok');
        // Sensible default tab: leads if any, otherwise widget setup.
        setTab(leads.length > 0 ? 'inbox' : 'widget');
      } else {
        setForm({ ...EMPTY_FORM, contact_email: user.email ?? '' });
        // No widget yet → straight to setup.
        setTab('widget');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user.id, user.email]);

  // Auto-derive slug from brand_name until the user manually edits it.
  useEffect(() => {
    if (slugTouched) return;
    const next = slugify(form.brand_name);
    setForm((f) => (f.slug === next ? f : { ...f, slug: next }));
  }, [form.brand_name, slugTouched]);

  // Live slug availability check (debounced).
  useEffect(() => {
    if (!form.slug) {
      setSlugStatus('idle');
      return;
    }
    setSlugStatus('checking');
    const t = window.setTimeout(async () => {
      const ok = await isSlugAvailable(form.slug);
      setSlugStatus(ok ? 'ok' : 'taken');
    }, 350);
    return () => window.clearTimeout(t);
  }, [form.slug]);

  const shareUrl = useMemo(
    () => (form.slug ? publicUrlForSlug(form.slug) : ''),
    [form.slug]
  );

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.brand_name.trim()) {
      setError('Please enter a brand name (e.g. your agency name).');
      return;
    }
    if (slugStatus !== 'ok') {
      setError("That URL isn't available — please tweak the slug.");
      return;
    }

    setSaving(true);
    try {
      const r = await upsertMyWidget(user.id, {
        brand_name: form.brand_name.trim(),
        slug: form.slug.trim().toLowerCase(),
        primary_colour: form.primary_colour || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        office_address: form.office_address.trim() || null,
        logo_url: form.logo_url.trim() || null,
        disclaimer_text: form.disclaimer_text.trim() || null,
        status: 'active',
      });
      if (!r.success || !r.widget) {
        setError(r.error ?? 'Failed to save.');
        return;
      }
      setWidget(r.widget);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const onCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // ignore — user can select manually
    }
  };

  if (loading) {
    return (
      <main className="flex-1 px-4 py-12">
        <div className="max-w-4xl mx-auto flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="animate-spin" size={16} />
          Loading your widget…
        </div>
      </main>
    );
  }

  const accent = form.primary_colour || '#f47a6b';

  return (
    <main className="flex-1 px-4 py-10">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
              Dashboard
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {tab === 'inbox'
                ? 'Inbox'
                : widget
                ? 'Your Valmate widget'
                : 'Set up your Valmate widget'}
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {tab === 'inbox'
                ? 'Every valuation request from your widget — captured, attributed, and ready for follow-up.'
                : widget
                ? 'Edit your branding and share your valuation page anywhere — your website, social, email signature.'
                : "Brand your shareable valuation page so it looks like it's part of your agency."}
            </p>
          </div>

          {widget && <TierChip widget={widget} />}
        </div>

        {/* Tabs */}
        {widget && (
          <div className="flex items-center gap-1 mb-6 border-b border-slate-800">
            <TabButton
              active={tab === 'inbox'}
              onClick={() => setTab('inbox')}
              icon={<InboxIcon size={14} />}
              label="Inbox"
              badge={openLeadCount > 0 ? openLeadCount : undefined}
            />
            <TabButton
              active={tab === 'widget'}
              onClick={() => setTab('widget')}
              icon={<Settings size={14} />}
              label="Widget"
            />
          </div>
        )}

        {tab === 'inbox' && widget && (
          <Inbox
            shareUrl={widget.slug ? publicUrlForSlug(widget.slug) : null}
            onGoToWidget={() => setTab('widget')}
          />
        )}

        {(tab === 'widget' || !widget) && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form */}
          <form
            onSubmit={onSave}
            className="lg:col-span-3 p-6 rounded-2xl bg-slate-900/50 border border-slate-800 space-y-4"
          >
            <Field label="Agency / brand name *">
              <input
                required
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                placeholder="Smith & Co Estate Agents"
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-slate-600"
              />
            </Field>

            <Field
              label="Public URL slug *"
              hint="3–32 characters · lowercase letters, numbers, dashes"
            >
              <div className="flex items-stretch rounded-lg bg-slate-950 border border-slate-800 overflow-hidden">
                <span className="px-3 py-2.5 text-xs text-slate-500 bg-slate-900 border-r border-slate-800 self-center">
                  /v/
                </span>
                <input
                  required
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm({
                      ...form,
                      slug: e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, '')
                        .slice(0, 32),
                    });
                  }}
                  placeholder="smith-and-co"
                  className="flex-1 px-3 py-2.5 bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none"
                />
                <SlugStatusIcon status={slugStatus} />
              </div>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Primary brand colour">
                <div className="flex items-stretch rounded-lg bg-slate-950 border border-slate-800 overflow-hidden">
                  <input
                    type="color"
                    value={form.primary_colour}
                    onChange={(e) => setForm({ ...form, primary_colour: e.target.value })}
                    className="w-12 h-10 bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.primary_colour}
                    onChange={(e) =>
                      setForm({ ...form, primary_colour: e.target.value })
                    }
                    placeholder="#f47a6b"
                    className="flex-1 px-3 py-2.5 bg-transparent text-sm text-white font-mono placeholder-slate-500 focus:outline-none"
                  />
                </div>
              </Field>

              <Field label="Logo URL" hint="Hosted PNG / SVG. Upload coming soon.">
                <input
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="https://yoursite.co.uk/logo.png"
                  className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-slate-600"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Contact phone">
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="01702 123 456"
                  className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-slate-600"
                />
              </Field>
              <Field label="Contact email">
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  placeholder="hello@yourname.co.uk"
                  className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-slate-600"
                />
              </Field>
            </div>

            <Field label="Office address">
              <input
                value={form.office_address}
                onChange={(e) => setForm({ ...form, office_address: e.target.value })}
                placeholder="12 High Street, Southend-on-Sea, SS1 1AA"
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-slate-600"
              />
            </Field>

            <Field label="Footer disclaimer (optional)">
              <textarea
                value={form.disclaimer_text}
                onChange={(e) => setForm({ ...form, disclaimer_text: e.target.value })}
                rows={2}
                placeholder="Smith & Co Estate Agents Ltd is registered in England & Wales (12345678)."
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-slate-600 resize-none"
              />
            </Field>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-900/30 border border-rose-700/40 text-sm text-rose-200">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
              <div className="text-xs text-slate-500">
                {savedAt ? (
                  <span className="inline-flex items-center gap-1 text-emerald-400">
                    <CheckCircle2 size={12} />
                    Saved
                  </span>
                ) : null}
              </div>
              <button
                type="submit"
                disabled={saving || slugStatus !== 'ok'}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-50 transition-colors"
                style={{ background: accent }}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {widget ? 'Save changes' : 'Create my widget'}
              </button>
            </div>
          </form>

          {/* Share URL panel */}
          <aside className="lg:col-span-2 space-y-4">
            <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                <Sparkles size={12} />
                Your shareable URL
              </div>
              {shareUrl ? (
                <>
                  <div className="px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 font-mono text-xs text-white break-all mb-3">
                    {shareUrl}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={onCopy}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold transition-colors"
                    >
                      {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <a
                      href={shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-white text-xs font-semibold transition-colors"
                      style={{ background: accent }}
                    >
                      <ExternalLink size={13} />
                      Preview
                    </a>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                    Share this link anywhere — your website, social, email signature.
                    Every valuation lead lands in your inbox at{' '}
                    <span className="text-slate-300">
                      {form.contact_email || user.email}
                    </span>
                    .
                  </p>
                </>
              ) : (
                <p className="text-xs text-slate-500">
                  Pick a slug to preview your shareable URL.
                </p>
              )}
            </div>

            <div className="p-5 rounded-2xl bg-slate-900/30 border border-slate-800 opacity-80">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Embed snippet
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                A drop-in <code className="text-slate-400">&lt;script&gt;</code>{' '}
                tag for your website (with iframe fallback) is coming next.
                For now, link to your URL from a "Get a free valuation" button.
              </p>
            </div>
          </aside>
        </div>
        )}
      </div>
    </main>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
        active
          ? 'text-white'
          : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      {icon}
      {label}
      {badge !== undefined && badge > 0 && (
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
          style={{
            background: 'rgba(244, 122, 107, 0.18)',
            color: '#f9a397',
          }}
        >
          {badge}
        </span>
      )}
      {active && (
        <span
          className="absolute left-0 right-0 -bottom-px h-0.5"
          style={{ background: '#f47a6b' }}
        />
      )}
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium text-slate-300">{label}</span>
        {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function TierChip({ widget }: { widget: AgentWidget }) {
  const tier = widget.plan_tier;
  const source = widget.tier_source;

  const labels: Record<typeof tier, string> = {
    free: 'Free tier',
    pro: 'Pro',
    agency: 'Agency',
    agency_plus: 'Agency+',
  };

  const isFree = tier === 'free';
  const isBundled = source === 'doorsteps_bundle';

  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      <div className="flex items-center gap-2">
        <div
          className="text-[11px] uppercase tracking-wider font-semibold text-slate-500"
        >
          Plan
        </div>
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
            isFree
              ? 'bg-slate-900 text-slate-300 border-slate-800'
              : 'border-[#f47a6b]/40 text-[#f9a397]'
          }`}
          style={
            !isFree
              ? { background: 'rgba(244, 122, 107, 0.12)' }
              : undefined
          }
        >
          {labels[tier]}
          {isBundled && tier !== 'free' && ' · via Doorsteps'}
        </span>
      </div>
      {isFree && (
        <a
          href="/pricing"
          className="text-[11px] font-semibold inline-flex items-center gap-1 hover:underline"
          style={{ color: '#f47a6b' }}
        >
          See plans →
        </a>
      )}
    </div>
  );
}

function SlugStatusIcon({ status }: { status: 'idle' | 'checking' | 'ok' | 'taken' | 'invalid' }) {
  const cls = 'flex items-center px-3 text-xs';
  if (status === 'checking') {
    return (
      <div className={`${cls} text-slate-500`}>
        <Loader2 size={12} className="animate-spin" />
      </div>
    );
  }
  if (status === 'ok') {
    return (
      <div className={`${cls} text-emerald-400`} title="Available">
        <CheckCircle2 size={14} />
      </div>
    );
  }
  if (status === 'taken' || status === 'invalid') {
    return (
      <div className={`${cls} text-rose-400`} title="Not available">
        <AlertCircle size={14} />
      </div>
    );
  }
  return null;
}
